import { useContext, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { Headline500Skin } from '../../atoms/Headline/HeadlineSkins';
import { Copytext } from '../../atoms/Copytext';
import { CopyStandardSkin } from '../../atoms/Copytext/CopytextSkins';
import recommendationsApi from '~/api/recommendations';
import JobTeaserOverview from '../JobTeaserOverview';
import CTA from '../../atoms/CTA';
import { CTAIconPosition } from '../../atoms/CTA/CTA.constants';
import UrlContext from '~/helpers/UrlContext';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
`;

const TeasersWrapper = styled.div`
  margin-bottom: 35px;
`;

const NoResultsWrapper = styled.div`
  display: grid;
  gap: 15px;
`;

const HeadlineStyled = styled.h4`
  ${Headline500Skin};
`;
const CopyStyled = styled(Copytext)`
  ${CopyStandardSkin};
`;

/**
 * @param {CmsJobTeaserOverviewProps} props
 */
const CmsJobTeaserOverview = ({
  language,
  config,
  format,
  landingPageCode,
  isBrand,
}) => {
  const createUrl = useContext(UrlContext);
  const intl = useIntl();
  const [teasersResponse, setTeasersResponse] = useState();
  const [encodedParameters, setEncodedParameters] = useState();

  const getJobSearchUrl = () => {
    if (teasersResponse?.jobSearch && teasersResponse?.jobSearch?.count > 0) {
      return createUrl(teasersResponse.jobSearch.url);
    }
    return createUrl(
      intl.formatMessage({
        id: 'job.search.url',
      }),
    );
  };

  const getTeasers = async () => {
    try {
      const recommendationsResponse = await recommendationsApi.get({
        language,
        type: 'JOB_AD',
        config,
        landingPageCode,
      });
      if (recommendationsResponse.status === 'OK') {
        setTeasersResponse(recommendationsResponse.data);
      }
    } catch (err) {
      console.log(err); // eslint-disable-line no-console
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!encodedParameters || config?.encodedParameters !== encodedParameters) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      getTeasers();

      setEncodedParameters(config?.encodedParameters);
    }
  }, [config]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const ctaText = intl.formatMessage(
    { id: 'jobAdRecommendationsText' },
    { jobsCount: String(teasersResponse?.jobSearch.count) },
  );

  return (
    <Wrapper>
      {teasersResponse && teasersResponse?.jobAds.length > 0 && (
        <TeasersWrapper>
          <JobTeaserOverview
            teasers={teasersResponse?.jobAds}
            format={format}
            isDarkMode={isBrand}
          />
        </TeasersWrapper>
      )}
      {teasersResponse && teasersResponse?.jobAds.length === 0 && (
        <NoResultsWrapper>
          <HeadlineStyled>
            {intl.formatMessage({
              id: 'job.overview.no.results.title',
            })}
          </HeadlineStyled>
          <CopyStyled>
            {intl.formatMessage({
              id: 'job.overview.no.results.content',
            })}
          </CopyStyled>
          <CTA
            text={intl.formatMessage({
              id: 'job.search',
            })}
            href={getJobSearchUrl()}
            title={intl.formatMessage({
              id: 'job.search',
            })}
            skin="PRIMARY"
            iconPos={CTAIconPosition.Before}
            icon="SEARCH"
            isBrand={isBrand}
          />
        </NoResultsWrapper>
      )}
      {teasersResponse &&
        teasersResponse.jobSearch &&
        teasersResponse.jobSearch.count > 0 && (
          <CTA
            text={ctaText}
            href={getJobSearchUrl()}
            title={ctaText}
            icon="ARROW_ANIMATION"
            iconPos={CTAIconPosition.Before}
            skin="TERTIARY"
            isBrand={isBrand}
          />
        )}
    </Wrapper>
  );
};

export default CmsJobTeaserOverview;
